import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["allowMultiSelection", "id", "label", "subtitle", "icon", "inlineIcon", "nestingLevel", "isSelected", "isIndeterminate", "isDisabled", "isExpandable", "isExpanded", "onSelect", "onToggleExpand"];
import "core-js/modules/es.array.concat.js";
import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import useUniqueId from 'hooks/useUniqueId';
import Box from 'components/Box';
import Button from 'components/Button';
import { Checkbox } from 'components/CheckboxOption';
import Flex from 'components/Flex';
import OverflowTooltip from 'components/OverflowTooltip';
import { RadioButton } from 'components/RadioButtonOption';
import Text from 'components/Text';
import SelectionIndicator from 'components/ItemPicker/ListItem/SelectionIndicator';
import LabelBox from 'components/ItemPicker/ListItem/LabelBox';
function TreeViewItem(_ref) {
  var _ref$allowMultiSelect = _ref.allowMultiSelection,
    allowMultiSelection = _ref$allowMultiSelect === void 0 ? true : _ref$allowMultiSelect,
    id = _ref.id,
    label = _ref.label,
    subtitle = _ref.subtitle,
    icon = _ref.icon,
    inlineIcon = _ref.inlineIcon,
    _ref$nestingLevel = _ref.nestingLevel,
    nestingLevel = _ref$nestingLevel === void 0 ? 0 : _ref$nestingLevel,
    isSelected = _ref.isSelected,
    isIndeterminate = _ref.isIndeterminate,
    isDisabled = _ref.isDisabled,
    isExpandable = _ref.isExpandable,
    isExpanded = _ref.isExpanded,
    onSelect = _ref.onSelect,
    onToggleExpand = _ref.onToggleExpand,
    otherProps = _objectWithoutProperties(_ref, _excluded);
  var _useTranslation = useTranslation(),
    t = _useTranslation.t;
  var itemRef = useRef(null);
  var uniqueId = useUniqueId(id ? String(id) : undefined);
  var itemId = "list-item-".concat(uniqueId);
  var labelId = "list-item-label-".concat(uniqueId);
  var expandButtonId = "list-item-expand-".concat(uniqueId);
  var subtitleId = subtitle ? "list-item-subtitle-".concat(uniqueId) : undefined;
  var shouldAppearChecked = !isDisabled && (isSelected || isIndeterminate);
  var hasExpansionButton = isExpandable !== undefined;
  function handleRowClick(e) {
    // We're handling changes on the wrapper element to make the whole
    // item picker row clickable.
    // Originally we were using a pseudo element inside of the <label>
    // that covered the row area, but this prevented using other
    // pointer events within the row, e.g. for tooltips
    e.preventDefault();
    e.stopPropagation();
    if (!isDisabled) {
      var _itemRef$current;
      // We'll return the item and the new state the checkbox should be in.
      // Indeterminate checkboxes should always return to a checked state first.
      onSelect(isIndeterminate || !isSelected);
      (_itemRef$current = itemRef.current) === null || _itemRef$current === void 0 ? void 0 : _itemRef$current.focus();
    }
  }
  function toggleExpand(e) {
    e.preventDefault();
    e.stopPropagation();
    onToggleExpand === null || onToggleExpand === void 0 ? void 0 : onToggleExpand(!isExpanded);
  }
  return /*#__PURE__*/React.createElement(Flex, _extends({
    position: "relative",
    height: 48,
    borderBottom: "thin"
  }, otherProps, {
    p: "xxs",
    pl: 32 * nestingLevel,
    onClick: handleRowClick
  }), shouldAppearChecked && /*#__PURE__*/React.createElement(SelectionIndicator, null), hasExpansionButton && /*#__PURE__*/React.createElement(Box, {
    ml: "xs",
    mr: "-m",
    width: 42
  }, isExpandable && /*#__PURE__*/React.createElement(Button, {
    iconOnly: true,
    variant: "transparent",
    id: expandButtonId,
    icon: isExpanded ? 'nav-down' : 'nav-right',
    onClick: toggleExpand,
    "aria-expanded": isExpanded,
    "aria-labelledby": "".concat(expandButtonId, " ").concat(labelId)
  }, t('actions_open'))), /*#__PURE__*/React.createElement(Box, {
    position: "relative",
    display: "flex",
    z: "raised",
    ml: "l",
    mr: "m"
  }, allowMultiSelection ? /*#__PURE__*/React.createElement(Checkbox, {
    id: itemId,
    ref: itemRef,
    checked: isSelected,
    disabled: isDisabled,
    indeterminate: isIndeterminate,
    "aria-describedby": subtitleId,
    onChange: function onChange(e) {
      return e.preventDefault();
    }
  }) : /*#__PURE__*/React.createElement(RadioButton, {
    id: itemId,
    value: id,
    ref: itemRef,
    checked: isSelected,
    disabled: isDisabled,
    "aria-describedby": subtitleId,
    onChange: function onChange(e) {
      return e.preventDefault();
    }
  })), icon && /*#__PURE__*/React.createElement(Box, {
    mr: "s",
    style: isDisabled ? {
      opacity: 0.5
    } : {
      opacity: 1
    }
  }, icon), inlineIcon && /*#__PURE__*/React.createElement(Box, {
    mr: "xxs",
    style: isDisabled ? {
      opacity: 0.5
    } : {
      opacity: 1
    }
  }, inlineIcon), /*#__PURE__*/React.createElement(Box, {
    grow: true,
    shrink: true,
    dimmed: isDisabled,
    basis: 0,
    lineHeight: 1.3,
    display: "flex",
    flexDirection: "column"
  }, /*#__PURE__*/React.createElement(OverflowTooltip, {
    content: label
  }, /*#__PURE__*/React.createElement(LabelBox, {
    as: "label",
    display: "block",
    overflow: "ellipsis",
    position: "relative",
    htmlFor: itemId,
    id: labelId,
    isChecked: shouldAppearChecked
  }, label)), subtitle && /*#__PURE__*/React.createElement(Text, {
    dimmed: true,
    size: "xs",
    overflow: "ellipsis",
    id: subtitleId
  }, subtitle)));
}
export default TreeViewItem;